import {
  beoordeelOmgeving,
  omschrijf,
  type AppIdentiteit,
  type Grendelbevinding,
} from "./omgevingsGrendel";

/**
 * Zet de omgevingsgrendel vóór de applicatie start.
 *
 * Draait als ALLEREERSTE in elke `main.tsx`: er wordt niets gemount, geen router
 * opgebouwd en geen Supabase-aanroep gedaan wanneer de combinatie niet klopt.
 * Dat is het verschil met een controle binnen de app — daar heeft de sessie zich
 * dan al hersteld en is er al een auth-request onderweg.
 *
 * Bewust zonder React: dit moet werken vóór er ook maar iets gemount is.
 */
export function grendelOmgeving(app: AppIdentiteit, rootEl: HTMLElement): boolean {
  const bevinding = beoordeelOmgeving({
    app,
    origin: typeof window === "undefined" ? "" : window.location.origin,
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL as string | undefined,
    isolatedPreviewProjectRef: import.meta.env.DEV
      ? import.meta.env.VITE_ISOLATED_PREVIEW_PROJECT_REF as string | undefined
      : undefined,
  });

  if (bevinding.toegestaan) return true;

  toonBlokkade(rootEl, bevinding);
  return false;
}

/** Los toetsbaar: bouwt het foutscherm zonder ooit een sleutel of token te tonen. */
export function blokkadeTekst(bevinding: Extract<Grendelbevinding, { toegestaan: false }>): {
  titel: string;
  uitleg: string;
  regels: string[];
} {
  return {
    titel: "Deze omgeving klopt niet",
    uitleg: `${bevinding.uitleg} Er wordt niets geladen en er is niets opgeslagen.`,
    regels: [
      `Applicatie: ${omschrijf(bevinding.app)}`,
      `Adres: ${bevinding.host}`,
      `Supabase-project: ${bevinding.projectGemaskeerd}`,
      `Reden: ${bevinding.code}`,
    ],
  };
}

function toonBlokkade(
  rootEl: HTMLElement,
  bevinding: Extract<Grendelbevinding, { toegestaan: false }>,
): void {
  const { titel, uitleg, regels } = blokkadeTekst(bevinding);

  /*
   * Met de DOM-API opgebouwd en niet met innerHTML: de host komt uit de
   * adresbalk en is dus invoer van buiten. `textContent` sluit injectie uit.
   */
  const kaart = document.createElement("div");
  kaart.setAttribute(
    "style",
    "max-width:34rem;margin:12vh auto;padding:1.5rem;border:1px solid #fecaca;" +
      "border-radius:0.75rem;background:#fff1f2;color:#7f1d1d;" +
      "font:14px/1.55 system-ui,-apple-system,Segoe UI,sans-serif",
  );

  const kop = document.createElement("h1");
  kop.textContent = titel;
  kop.setAttribute("style", "margin:0 0 .5rem;font-size:1.05rem;font-weight:600");
  kaart.appendChild(kop);

  const p = document.createElement("p");
  p.textContent = uitleg;
  p.setAttribute("style", "margin:0 0 .9rem");
  kaart.appendChild(p);

  const lijst = document.createElement("ul");
  lijst.setAttribute("style", "margin:0;padding-left:1.1rem;font-family:ui-monospace,monospace");
  for (const regel of regels) {
    const li = document.createElement("li");
    li.textContent = regel;
    lijst.appendChild(li);
  }
  kaart.appendChild(lijst);

  const hint = document.createElement("p");
  hint.textContent =
    "Kom je hier via een oude tab of een bewaarde pagina? Ververs met een harde herlading. " +
    "Blijft dit staan, meld dan bovenstaande regels.";
  hint.setAttribute("style", "margin:.9rem 0 0;color:#9f1239");
  kaart.appendChild(hint);

  rootEl.replaceChildren(kaart);
}
